import { parseIntegerInput } from "@/lib/parse-number";
import {
  ceilDiv,
  type DayNumber,
  daysMatching,
  parseIsoDay,
  weekdayOf,
} from "@/lib/tool-kit/study/dates";

/**
 * The reading planner: split a page range over reading days.
 *
 * Pages are dealt by cumulative target — by the end of reading day *i* of *n* you have
 * read `ceil(i × pages ÷ n)` of them — so the days differ by at most one page and any
 * extra page falls early in the plan, which is where a reader wants their slack.
 */

export interface ReadingDay {
  day: DayNumber;
  /** First page to read today. */
  from: number;
  /** Last page to read today, inclusive. */
  to: number;
  pages: number;
}

/** Spread pages `first`–`last` (inclusive) over `days`. Days that get no page are dropped. */
export function splitPages(
  first: number,
  last: number,
  days: readonly DayNumber[]
): ReadingDay[] {
  const total = last - first + 1;
  const count = days.length;
  if (total <= 0 || count === 0) {
    return [];
  }
  const plan: ReadingDay[] = [];
  let read = 0;
  for (const [index, day] of days.entries()) {
    const target = ceilDiv((index + 1) * total, count);
    if (target > read) {
      plan.push({
        day,
        from: first + read,
        to: first + target - 1,
        pages: target - read,
      });
    }
    read = target;
  }
  return plan;
}

/**
 * The first `count` reading days on or after `start`.
 *
 * Gives up after `maxSpan` calendar days, so a plan with no reading weekday chosen
 * cannot loop forever; the caller sees a short list and says so.
 */
export function nextReadingDays(
  start: DayNumber,
  count: number,
  weekdays: ReadonlySet<number>,
  maxSpan: number
): DayNumber[] {
  const days: DayNumber[] = [];
  for (
    let day = start;
    day < start + maxSpan && days.length < count;
    day += 1
  ) {
    if (weekdays.has(weekdayOf(day))) {
      days.push(day);
    }
  }
  return days;
}

/** Read `perDay` pages a day, starting on `start`; the last day takes what is left. */
export function planAtPace(
  first: number,
  last: number,
  perDay: number,
  start: DayNumber,
  weekdays: ReadonlySet<number>,
  maxSpan: number
): ReadingDay[] {
  const total = last - first + 1;
  if (total <= 0 || perDay <= 0) {
    return [];
  }
  const days = nextReadingDays(
    start,
    ceilDiv(total, perDay),
    weekdays,
    maxSpan
  );
  const plan: ReadingDay[] = [];
  let read = 0;
  for (const day of days) {
    const pages = Math.min(perDay, total - read);
    plan.push({
      day,
      from: first + read,
      to: first + read + pages - 1,
      pages,
    });
    read += pages;
  }
  return plan;
}

/** Minutes to read `pages` at `wordsPerMinute`, given `wordsPerPage`. */
export function readingMinutes(
  pages: number,
  wordsPerPage: number,
  wordsPerMinute: number
): number {
  if (wordsPerMinute <= 0) {
    return 0;
  }
  return (pages * wordsPerPage) / wordsPerMinute;
}

export interface ReadingInputs {
  pagesMode: "total" | "range";
  total: string;
  from: string;
  to: string;
  planMode: "deadline" | "pace";
  start: string;
  finish: string;
  pace: string;
  /** `weekdayOf` numbers of the reading days. */
  weekdays: ReadonlySet<number>;
}

/** Why there is no plan yet: the first three are prompts, the rest are mistakes. */
export type ReadingProblem =
  | "enterPages"
  | "enterDates"
  | "enterPace"
  | "rangeBackwards"
  | "finishBeforeStart"
  | "noReadingDays"
  | "tooLong";

export const READING_ERRORS: ReadonlySet<ReadingProblem> = new Set([
  "rangeBackwards",
  "finishBeforeStart",
  "noReadingDays",
  "tooLong",
]);

export interface ReadingOutcome {
  plan: ReadingDay[];
  problem: ReadingProblem | null;
}

const MAX_PAGES = 100_000;

function pageBounds(
  inputs: ReadingInputs
): { first: number; last: number } | null {
  if (inputs.pagesMode === "total") {
    const total = parseIntegerInput(inputs.total);
    return total !== null && total > 0 && total <= MAX_PAGES
      ? { first: 1, last: total }
      : null;
  }
  const first = parseIntegerInput(inputs.from);
  const last = parseIntegerInput(inputs.to);
  if (first === null || last === null || first < 1 || last > MAX_PAGES) {
    return null;
  }
  return { first, last };
}

function byDeadline(
  bounds: { first: number; last: number },
  start: DayNumber,
  inputs: ReadingInputs,
  maxDays: number
): ReadingOutcome {
  const finish = parseIsoDay(inputs.finish);
  if (finish === null) {
    return { plan: [], problem: "enterDates" };
  }
  if (finish < start) {
    return { plan: [], problem: "finishBeforeStart" };
  }
  if (finish - start > maxDays) {
    return { plan: [], problem: "tooLong" };
  }
  const days = daysMatching(start, finish, inputs.weekdays);
  if (days.length === 0) {
    return { plan: [], problem: "noReadingDays" };
  }
  return { plan: splitPages(bounds.first, bounds.last, days), problem: null };
}

/** The reading planner's form, read and planned; `maxDays` caps how long a plan may run. */
export function buildReadingPlan(
  inputs: ReadingInputs,
  maxDays: number
): ReadingOutcome {
  const bounds = pageBounds(inputs);
  if (bounds === null) {
    return { plan: [], problem: "enterPages" };
  }
  if (bounds.last < bounds.first) {
    return { plan: [], problem: "rangeBackwards" };
  }
  const start = parseIsoDay(inputs.start);
  if (start === null) {
    return { plan: [], problem: "enterDates" };
  }
  if (inputs.planMode === "deadline") {
    return byDeadline(bounds, start, inputs, maxDays);
  }
  const pace = parseIntegerInput(inputs.pace);
  if (pace === null || pace <= 0) {
    return { plan: [], problem: "enterPace" };
  }
  if (inputs.weekdays.size === 0) {
    return { plan: [], problem: "noReadingDays" };
  }
  const plan = planAtPace(
    bounds.first,
    bounds.last,
    pace,
    start,
    inputs.weekdays,
    maxDays
  );
  const covered = plan.reduce((sum, day) => sum + day.pages, 0);
  if (covered < bounds.last - bounds.first + 1) {
    return { plan: [], problem: "tooLong" };
  }
  return { plan, problem: null };
}
